import React, { useEffect, useMemo, useRef, useState } from "react";
import DashboardLayout from "../components/layout/index";
import { toast } from "react-toastify";
import {
  ClipboardList,
  Search,
  Filter,
  Stethoscope,
  Hash,
  Tag,
  Activity,
  BadgeCheck,
  Plus,
  X,
  RotateCcw,
} from "lucide-react";

const EMPTY_FORM = {
  category: "",
  diagnosisName: "",
  icdCode: "",
  snomedCode: "",
  conditionType: "",
};

const inputClass =
  "h-10 w-full rounded-xl border border-slate-200 bg-white pl-9 pr-3 text-sm font-medium text-slate-700 placeholder:text-slate-400 focus:border-teal-500/50 focus:outline-none";
const formInputClass =
  "h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-teal-500/50 focus:outline-none";

const CurebayDiagnosisMaster = () => {
  const API_URL = process.env.REACT_APP_NEW_DOCTOR_URL;
  const DIAGNOSIS_URL = `${API_URL}/api/v1/diagnosis-master`;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const [categoryInput, setCategoryInput] = useState("");
  const [diagnosisNameInput, setDiagnosisNameInput] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [filters, setFilters] = useState({
    category: "",
    diagnosisName: "",
    search: "",
  });
  const debounceRef = useRef(null);

  // Category / condition type suggestions collected from loaded rows
  const [knownCategories, setKnownCategories] = useState([]);
  const [knownConditionTypes, setKnownConditionTypes] = useState([]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setFilters({
        category: categoryInput.trim(),
        diagnosisName: diagnosisNameInput.trim(),
        search: searchInput.trim(),
      });
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(debounceRef.current);
  }, [categoryInput, diagnosisNameInput, searchInput]);

  useEffect(() => {
    let isActive = true;
    const fetchDiagnoses = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        if (filters.category) params.set("category", filters.category);
        if (filters.diagnosisName)
          params.set("diagnosisName", filters.diagnosisName);
        if (filters.search) params.set("search", filters.search);
        const query = params.toString();
        const res = await fetch(`${DIAGNOSIS_URL}${query ? `?${query}` : ""}`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        const data = await res.json();
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
          ? data.data
          : [];
        if (!isActive) return;
        setRows(list);
        setKnownCategories((prev) =>
          Array.from(
            new Set([...prev, ...list.map((d) => d.category).filter(Boolean)])
          )
        );
        setKnownConditionTypes((prev) =>
          Array.from(
            new Set([
              ...prev,
              ...list.map((d) => d.conditionType).filter(Boolean),
            ])
          )
        );
      } catch (error) {
        console.error("Failed to fetch diagnosis list:", error);
        if (isActive) {
          setRows([]);
          toast.error("Unable to load diagnosis list.");
        }
      } finally {
        if (isActive) setLoading(false);
      }
    };

    fetchDiagnoses();
    return () => {
      isActive = false;
    };
  }, [DIAGNOSIS_URL, filters, reloadKey]);

  const totalElements = rows.length;
  const totalPages = Math.max(1, Math.ceil(totalElements / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const pageRows = useMemo(
    () => rows.slice(startIndex, startIndex + pageSize),
    [rows, startIndex]
  );
  const startEntry = totalElements === 0 ? 0 : startIndex + 1;
  const endEntry = Math.min(startIndex + pageSize, totalElements);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const visiblePages = () => {
    const pages = [];
    const maxVisible = 7;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i += 1) pages.push(i);
    } else if (safePage <= 4) {
      for (let i = 1; i <= maxVisible; i += 1) pages.push(i);
    } else if (safePage >= totalPages - 3) {
      for (let i = totalPages - maxVisible + 1; i <= totalPages; i += 1) {
        pages.push(i);
      }
    } else {
      for (let i = safePage - 3; i <= safePage + 3; i += 1) pages.push(i);
    }
    return pages;
  };

  const resetFilters = () => {
    setCategoryInput("");
    setDiagnosisNameInput("");
    setSearchInput("");
  };

  const openAddModal = () => {
    setForm(EMPTY_FORM);
    setFormErrors({});
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    if (saving) return;
    setShowAddModal(false);
  };

  const updateForm = (key) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
    setFormErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const validateForm = () => {
    const errors = {};
    if (!form.category.trim()) errors.category = "Category is required";
    if (!form.diagnosisName.trim())
      errors.diagnosisName = "Diagnosis name is required";
    if (!form.icdCode.trim()) errors.icdCode = "ICD code is required";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      category: form.category.trim(),
      diagnosisName: form.diagnosisName.trim(),
      icdCode: form.icdCode.trim(),
      snomedCode: form.snomedCode.trim(),
      conditionType: form.conditionType.trim(),
    };

    try {
      setSaving(true);
      const res = await fetch(DIAGNOSIS_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        let message = `Unable to add diagnosis (${res.status}).`;
        try {
          const errData = await res.json();
          if (errData?.message) message = errData.message;
        } catch (parseError) {
          // response had no JSON body
        }
        throw new Error(message);
      }
      toast.success("Diagnosis added successfully.");
      setShowAddModal(false);
      setReloadKey((k) => k + 1);
    } catch (error) {
      console.error("Failed to add diagnosis:", error);
      toast.error(error.message || "Unable to add diagnosis.");
    } finally {
      setSaving(false);
    }
  };

  const statusBadge = (status) =>
    Number(status) === 1 ? (
      <span className="inline-flex rounded-xl bg-emerald-50 px-3 py-1 text-sm font-medium text-emerald-700">
        Active
      </span>
    ) : (
      <span className="inline-flex rounded-xl bg-rose-50 px-3 py-1 text-sm font-medium text-rose-600">
        Inactive
      </span>
    );

  return (
    <DashboardLayout>
      <div className="mt-6 px-4">
        <div className="mb-8 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center rounded-xl border border-emerald-100 bg-emerald-50 p-2">
              <ClipboardList size={26} className="text-emerald-600" />
            </div>
            <h1 className="text-xl font-bold tracking-tight text-[#334155]">
              Diagnosis Master
            </h1>
          </div>
          <button
            type="button"
            onClick={openAddModal}
            className="inline-flex h-10 items-center gap-2 rounded-xl bg-teal-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-teal-700"
          >
            <Plus size={16} />
            Add Diagnosis
          </button>
        </div>

        {/* Filters */}
        <div className="mb-5 rounded-2xl border border-slate-100 bg-white px-4 py-4 shadow-sm sm:px-5">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_1fr_1fr_auto] md:items-end">
            <div className="flex flex-col gap-1">
              <label className="text-[11px] font-black uppercase tracking-widest text-slate-400">
                Category
              </label>
              <div className="relative">
                <Filter
                  size={14}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-teal-600"
                />
                <input
                  type="text"
                  list="diagnosis-category-options"
                  value={categoryInput}
                  onChange={(e) => setCategoryInput(e.target.value)}
                  placeholder="e.g. Respiratory & ENT"
                  className={inputClass}
                />
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-[11px] font-black uppercase tracking-widest text-slate-400">
                Diagnosis Name
              </label>
              <div className="relative">
                <Stethoscope
                  size={14}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-teal-600"
                />
                <input
                  type="text"
                  value={diagnosisNameInput}
                  onChange={(e) => setDiagnosisNameInput(e.target.value)}
                  placeholder="e.g. Otitis externa"
                  className={inputClass}
                />
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-[11px] font-black uppercase tracking-widest text-slate-400">
                Search
              </label>
              <div className="relative">
                <Search
                  size={14}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-teal-600"
                />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search by ICD code or name, e.g. H60"
                  className={inputClass}
                />
              </div>
            </div>

            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-600 hover:bg-slate-50"
            >
              <RotateCcw size={14} />
              Reset
            </button>
          </div>
          <datalist id="diagnosis-category-options">
            {knownCategories.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </div>

        {/* Table */}
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-sky-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">
                    #
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">
                    <div className="flex items-center gap-2">
                      <Tag size={14} className="text-emerald-600" />
                      Category
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">
                    <div className="flex items-center gap-2">
                      <Stethoscope size={14} className="text-teal-600" />
                      Diagnosis Name
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">
                    <div className="flex items-center gap-2">
                      <Hash size={14} className="text-emerald-600" />
                      ICD Code
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">
                    <div className="flex items-center gap-2">
                      <Hash size={14} className="text-emerald-600" />
                      SNOMED Code
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">
                    <div className="flex items-center gap-2">
                      <Activity size={14} className="text-emerald-600" />
                      Condition Type
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">
                    <div className="flex items-center gap-2">
                      <BadgeCheck size={14} className="text-emerald-600" />
                      Status
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {loading ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-6 py-10 text-center text-sm text-slate-500"
                    >
                      Loading diagnoses...
                    </td>
                  </tr>
                ) : pageRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-6 py-10 text-center text-sm text-slate-500"
                    >
                      No diagnoses found for the selected filters.
                    </td>
                  </tr>
                ) : (
                  pageRows.map((row, index) => (
                    <tr
                      key={row.id || `${row.icdCode}-${index}`}
                      className={index % 2 === 0 ? "bg-white" : "bg-slate-50/50"}
                    >
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-slate-500">
                        {startIndex + index + 1}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-slate-700">
                        {row.category || "-"}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-slate-900">
                        {row.diagnosisName || "-"}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm">
                        <span className="inline-flex rounded-lg bg-slate-100 px-3 py-1 font-medium text-slate-700">
                          {row.icdCode || "-"}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-slate-700">
                        {row.snomedCode || "-"}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-slate-700">
                        {row.conditionType || "-"}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm">
                        {statusBadge(row.status)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination footer */}
          <div className="flex flex-col items-center justify-between gap-3 border-t border-slate-200 bg-white px-6 py-4 text-sm text-slate-600 sm:flex-row">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => goToPage(safePage - 1)}
                disabled={safePage === 1}
                className={`px-2 py-1 text-sm ${
                  safePage === 1
                    ? "cursor-not-allowed text-slate-400"
                    : "text-slate-700 hover:text-slate-900"
                }`}
              >
                &lt; Prev
              </button>
              <div className="flex items-center gap-1">
                {visiblePages().map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => goToPage(p)}
                    className={`h-8 min-w-8 rounded-lg border px-2 text-sm ${
                      p === safePage
                        ? "border-blue-300 bg-blue-50 text-blue-700"
                        : "border-slate-300 text-slate-600 hover:text-slate-800"
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => goToPage(safePage + 1)}
                disabled={safePage === totalPages}
                className={`px-2 py-1 text-sm ${
                  safePage === totalPages
                    ? "cursor-not-allowed text-slate-400"
                    : "text-slate-700 hover:text-slate-900"
                }`}
              >
                Next &gt;
              </button>
            </div>
            <div className="text-xs text-slate-500 sm:text-sm">
              Showing {startEntry} to {endEntry} of {totalElements} entries
            </div>
          </div>
        </div>
      </div>

      {/* Add Diagnosis modal */}
      {showAddModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={closeAddModal}
        >
          <form
            onSubmit={handleSave}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg rounded-2xl bg-white shadow-xl"
          >
            <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
              <h2 className="text-lg font-bold text-[#334155]">Add Diagnosis</h2>
              <button
                type="button"
                onClick={closeAddModal}
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                <X size={18} />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-4 px-6 py-5 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="mb-1 block text-sm font-medium text-slate-600">
                  Category <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  list="diagnosis-category-options"
                  value={form.category}
                  onChange={updateForm("category")}
                  placeholder="e.g. Respiratory & ENT"
                  className={formInputClass}
                />
                {formErrors.category && (
                  <p className="mt-1 text-xs text-rose-500">{formErrors.category}</p>
                )}
              </div>

              <div className="sm:col-span-2">
                <label className="mb-1 block text-sm font-medium text-slate-600">
                  Diagnosis Name <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.diagnosisName}
                  onChange={updateForm("diagnosisName")}
                  placeholder="e.g. Acute laryngitis"
                  className={formInputClass}
                />
                {formErrors.diagnosisName && (
                  <p className="mt-1 text-xs text-rose-500">
                    {formErrors.diagnosisName}
                  </p>
                )}
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-slate-600">
                  ICD Code <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.icdCode}
                  onChange={updateForm("icdCode")}
                  placeholder="e.g. J04.0"
                  className={formInputClass}
                />
                {formErrors.icdCode && (
                  <p className="mt-1 text-xs text-rose-500">{formErrors.icdCode}</p>
                )}
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-slate-600">
                  SNOMED Code
                </label>
                <input
                  type="text"
                  value={form.snomedCode}
                  onChange={updateForm("snomedCode")}
                  placeholder="e.g. 6655004"
                  className={formInputClass}
                />
              </div>

              <div className="sm:col-span-2">
                <label className="mb-1 block text-sm font-medium text-slate-600">
                  Condition Type
                </label>
                <input
                  type="text"
                  list="diagnosis-condition-type-options"
                  value={form.conditionType}
                  onChange={updateForm("conditionType")}
                  placeholder="e.g. Acute / episodic"
                  className={formInputClass}
                />
                <datalist id="diagnosis-condition-type-options">
                  {knownConditionTypes.map((c) => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-slate-100 px-6 py-4">
              <button
                type="button"
                onClick={closeAddModal}
                disabled={saving}
                className="h-10 rounded-xl border border-slate-200 px-4 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="h-10 rounded-xl bg-teal-600 px-5 text-sm font-semibold text-white hover:bg-teal-700 disabled:opacity-60"
              >
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </form>
        </div>
      )}
    </DashboardLayout>
  );
};

export default CurebayDiagnosisMaster;
